import React, { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { XCircle, Loader2, Package, Search, CheckCircle, AlertCircle } from 'lucide-react';
import { insumoCorrespondeTermo } from '../utils/insumoBusca';

/** Filtro por tipo no modal de associação ao fornecedor (migração de vínculos). Desligado: reorganização de tipo é na página Insumos. */
export const EXIBIR_HELPER_FILTRO_TIPO_INSUMO = false;

function tipoInsumoNormalizado(doc) {
  const t = doc?.tipo_insumo;
  if (!t || t === 'materia_prima') return 'ingrediente';
  return t;
}

const AssociateProductsModal = ({
  isOpen,
  onClose,
  supplierId,
  supplierName,
  companyId,
  /** Ao abrir pelo menu Fornecedores: pré-seleciona o tipo de catálogo conforme a aba */
  filtroTipoCatalogoInicial = 'todos',
}) => {
  const [materiasPrimasCliente, setMateriasPrimasCliente] = useState([]);
  const [materiasPrimasFornecedor, setMateriasPrimasFornecedor] = useState([]);
  const [selectedMaterias, setSelectedMaterias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('');
  /** todos | ingrediente | embalagem | servico | limpeza_conservacao */
  const [filtroTipoCatalogo, setFiltroTipoCatalogo] = useState('todos');

  useEffect(() => {
    if (!isOpen) {
      resetState();
      return;
    }

    console.log('🔍 AssociateProductsModal: useEffect executado', {
      isOpen,
      supplierId,
      companyId
    });

    if (!supplierId) {
      console.error('❌ AssociateProductsModal: supplierId não informado');
      setError('Fornecedor não informado.');
      return;
    }

    if (!companyId) {
      console.error('❌ AssociateProductsModal: companyId não informado');
      setError('Empresa do cliente não informada.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setSelectedMaterias([]);
    setFiltroTipoCatalogo(
      filtroTipoCatalogoInicial === 'ingrediente' ||
        filtroTipoCatalogoInicial === 'embalagem' ||
        filtroTipoCatalogoInicial === 'servico' ||
        filtroTipoCatalogoInicial === 'limpeza_conservacao'
        ? filtroTipoCatalogoInicial
        : 'todos'
    );

    console.log('🔍 AssociateProductsModal: Iniciando carregamento...', {
      companyId,
      supplierId
    });

    loadMateriasPrimasCliente(companyId)
      .then((materiasCliente) => {
        console.log(`✅ AssociateProductsModal: ${materiasCliente.length} matérias-primas do cliente carregadas`);
        return loadMateriasPrimasFornecedor(supplierId);
      })
      .then((materiasFornecedor) => {
        console.log(`✅ AssociateProductsModal: ${materiasFornecedor.length} matérias-primas do fornecedor carregadas`);
      })
      .catch((err) => {
        console.error('❌ AssociateProductsModal: Erro ao carregar matérias-primas:', err);
        setError(err.message || 'Erro ao carregar o catálogo de insumos.');
      })
      .finally(() => {
        setLoading(false);
        console.log('🔍 AssociateProductsModal: Carregamento finalizado');
      });
  }, [isOpen, supplierId, companyId, filtroTipoCatalogoInicial]);

  useEffect(() => {
    // ✅ CORRIGIDO: Executar mesmo se fornecedor não tiver produtos (para marcar vazio)
    if (materiasPrimasCliente.length === 0) {
      return;
    }

    // ✅ SIMPLIFICADO: Comparar apenas por código (sem verificação de base global)
    const fornecedorCodigos = new Set(
      materiasPrimasFornecedor
        .map((item) => item.codigo || item.codigo_produto || item.codigo_origem)
        .filter(Boolean)
    );

    const selecionadas = materiasPrimasCliente.reduce((acc, materia) => {
      const codigo = materia.codigo || materia.codigo_produto || materia.codigo_origem;
      const identificador = getMateriaIdentifier(materia);

      if (identificador && codigo && fornecedorCodigos.has(codigo)) {
        acc.add(identificador);
      }
      return acc;
    }, new Set());

    setSelectedMaterias(Array.from(selecionadas));
  }, [materiasPrimasCliente, materiasPrimasFornecedor]);

  const resetState = () => {
    setMateriasPrimasCliente([]);
    setMateriasPrimasFornecedor([]);
    setSelectedMaterias([]);
    setError('');
    setSuccess('');
    setSearchTerm('');
    setFilterCategoria('');
    setFiltroTipoCatalogo('todos');
    setLoading(false);
    setImporting(false);
  };

  const loadMateriasPrimasCliente = async (clienteId) => {
    const listProducts = httpsCallable(functions, 'listProducts');
    const result = await listProducts({
      companyId: clienteId,
      page: 1,
      limit: 1000,
      ativo: true
    });

    if (!result.data?.success) {
      throw new Error(result.data?.message || 'Falha ao listar insumos do cliente.');
    }

    const materias = result.data.data.products || result.data.data.items || [];
    setMateriasPrimasCliente(materias);
    return materias;
  };

  const loadMateriasPrimasFornecedor = async (fornecedorId) => {
    if (!fornecedorId) {
      console.warn('⚠️ loadMateriasPrimasFornecedor: fornecedorId não informado');
      setMateriasPrimasFornecedor([]);
      return [];
    }

    try {
      const fn = httpsCallable(functions, 'listProdutosRecebimentoPorFornecedor');
      const result = await fn({
        fornecedor_id: fornecedorId,
        empresa_id: companyId,
        limit: 1500
      });

      if (!result.data?.success) {
        throw new Error(result.data?.message || 'Falha ao listar insumos do fornecedor.');
      }

      const materias = result.data.data.products || result.data.data.items || [];
      console.log(`✅ loadMateriasPrimasFornecedor: ${materias.length} matérias-primas carregadas para fornecedor ${fornecedorId}`);
      setMateriasPrimasFornecedor(materias);
      return materias;
    } catch (error) {
      console.error('❌ Erro ao carregar matérias-primas do fornecedor:', error);
      setMateriasPrimasFornecedor([]);
      throw error;
    }
  };

  const getFilteredMaterias = () => {
    return materiasPrimasCliente.filter((materia) => {
      const nome = materia.nome || materia.nome_produto || '';
      const codigo = materia.codigo || materia.codigo_produto || materia.codigo_origem || '';

      const matchesTipo =
        filtroTipoCatalogo === 'todos' ||
        tipoInsumoNormalizado(materia) === filtroTipoCatalogo;

      const matchesSearch = insumoCorrespondeTermo(materia, searchTerm);

      const matchesCategory =
        !filterCategoria || materia.categoria === filterCategoria;

      return matchesTipo && matchesSearch && matchesCategory;
    });
  };

  const labelTipoBadge = (m) => {
    const t = tipoInsumoNormalizado(m);
    if (t === 'embalagem') return 'Embalagem';
    if (t === 'servico') return 'Serviço';
    if (t === 'limpeza_conservacao') return 'Limpeza e conservação';
    return 'Ingrediente';
  };

  const textoItemLista = useMemo(() => {
    if (filtroTipoCatalogo === 'embalagem') return 'embalagem(ns)';
    if (filtroTipoCatalogo === 'servico') return 'serviço(s)';
    if (filtroTipoCatalogo === 'ingrediente') return 'ingrediente(s)';
    if (filtroTipoCatalogo === 'limpeza_conservacao') return 'item(ns) de limpeza e conservação';
    return 'item(ns)';
  }, [filtroTipoCatalogo]);

  /** Texto para confirmação (plural legível, sem abreviações entre parênteses) */
  const rotuloPluralConfirmacao = useMemo(() => {
    if (filtroTipoCatalogo === 'embalagem') return 'embalagem(ns)';
    if (filtroTipoCatalogo === 'servico') return 'serviço(s)';
    if (filtroTipoCatalogo === 'ingrediente') return 'ingrediente(s)';
    if (filtroTipoCatalogo === 'limpeza_conservacao') return 'item(ns) de limpeza e conservação';
    return 'insumo(s)';
  }, [filtroTipoCatalogo]);

  const categoriasFiltradas = useMemo(() => {
    const base =
      filtroTipoCatalogo === 'todos'
        ? materiasPrimasCliente
        : materiasPrimasCliente.filter(
            (m) => tipoInsumoNormalizado(m) === filtroTipoCatalogo
          );
    return [...new Set(base.map((m) => m.categoria).filter(Boolean))];
  }, [materiasPrimasCliente, filtroTipoCatalogo]);

  // ✅ SIMPLIFICADO: Usar apenas ID do produto da empresa
  const getMateriaIdentifier = (materia) => materia.id;

  const toggleMateriaSelection = (materia) => {
    const materiaId = getMateriaIdentifier(materia);
    if (!materiaId) {
      setError('Item do catálogo sem ID válido.');
      return;
    }

    setSelectedMaterias((prev) =>
      prev.includes(materiaId)
        ? prev.filter((id) => id !== materiaId)
        : [...prev, materiaId]
    );
  };

  const toggleSelectAll = () => {
    const filtered = getFilteredMaterias();
    const ids = filtered
      .map((materia) => getMateriaIdentifier(materia))
      .filter(Boolean);

    if (
      ids.length > 0 &&
      ids.every((id) => selectedMaterias.includes(id))
    ) {
      setSelectedMaterias([]);
    } else {
      setSelectedMaterias(ids);
    }
  };

  const handleAssociateMaterias = async () => {
    if (selectedMaterias.length === 0) {
      setError('Selecione pelo menos um item do catálogo para associar.');
      return;
    }

    if (
      !window.confirm(
        `Associar ${selectedMaterias.length} ${rotuloPluralConfirmacao} ao fornecedor ${supplierName}?`
      )
    ) {
      return;
    }

    try {
      setImporting(true);
      setError('');
      setSuccess('');

      const associarProdutosAoFornecedor = httpsCallable(
        functions,
        'associarProdutosAoFornecedor'
      );

      const result = await associarProdutosAoFornecedor({
        supplier_id: supplierId,
        produto_ids: selectedMaterias
      });

      if (!result.data?.success) {
        throw new Error(result.data?.message || 'Erro ao associar insumos.');
      }

      const importedList = result.data.data?.imported_products || [];
      const reactivatedList = result.data.data?.reactivated_products || [];
      const removedList = result.data.data?.removed_products || [];
      const alreadyList = result.data.data?.already_associated || [];
      const errorList = result.data.data?.errors || [];

      // ✅ DEBUG: Log dos dados recebidos
      console.log('🔍 AssociateProductsModal - Dados recebidos:', {
        imported: importedList,
        reactivated: reactivatedList,
        removed: removedList,
        already: alreadyList,
        errors: errorList,
        message: result.data.data?.message
      });

      const formatList = (items) =>
        items.length > 3
          ? `${items.slice(0, 3).join(', ')} e mais ${items.length - 3}`
          : items.join(', ');

      let successMessage = result.data.data?.message || 'Operação concluída.';

      if (importedList.length > 0) {
        successMessage += `\n✅ Associadas: ${formatList(importedList)}`;
      }

      if (reactivatedList.length > 0) {
        successMessage += `\n🔄 Reativadas: ${formatList(reactivatedList)}`;
      }

      if (alreadyList.length > 0) {
        successMessage += `\nℹ️ Mantidas: ${formatList(alreadyList)}`;
      }

      if (removedList.length > 0) {
        successMessage += `\n🗑️ Removidas: ${formatList(removedList)}`;
      }

      setSuccess(successMessage);

      if (errorList.length > 0) {
        setError(
          `⚠️ Falha em alguns insumos:\n${errorList
            .slice(0, 5)
            .join('\n')}${errorList.length > 5 ? `\n... e mais ${errorList.length - 5}` : ''}`
        );
      }

      await loadMateriasPrimasFornecedor(supplierId);

      if (errorList.length === 0) {
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    } catch (err) {
      console.error('Erro ao associar matérias-primas:', err);
      setError(err.message || 'Erro ao associar insumos.');
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  const filteredMaterias = getFilteredMaterias();

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <Package className="h-5 w-5 mr-2" />
              Associar insumos ao fornecedor
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          {supplierName && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-700">
                <strong>Fornecedor:</strong> {supplierName}
              </p>
            </div>
          )}

          {EXIBIR_HELPER_FILTRO_TIPO_INSUMO && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
              <p className="font-medium text-amber-900">Filtro por tipo de insumo</p>
              <p className="mt-1 text-amber-800">
                Use o filtro abaixo para ver só <strong>ingredientes</strong>, <strong>embalagens</strong>,{' '}
                <strong>serviços</strong> ou <strong>limpeza e conservação</strong>, e associar cada fornecedor ao catálogo correto.
                Para ocultar este aviso e o seletor, defina{' '}
                <code className="rounded bg-white px-1 text-xs">EXIBIR_HELPER_FILTRO_TIPO_INSUMO</code> como{' '}
                <code className="rounded bg-white px-1 text-xs">false</code> em{' '}
                <code className="rounded bg-white px-1 text-xs">AssociateProductsModal.jsx</code>.
              </p>
            </div>
          )}

          {EXIBIR_HELPER_FILTRO_TIPO_INSUMO && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Catálogo da empresa (tipo de insumo)
              </label>
              <select
                value={filtroTipoCatalogo}
                onChange={(e) => setFiltroTipoCatalogo(e.target.value)}
                className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="todos">Todos os tipos</option>
                <option value="ingrediente">Apenas ingredientes</option>
                <option value="embalagem">Apenas embalagens</option>
                <option value="servico">Apenas serviços</option>
                <option value="limpeza_conservacao">Apenas limpeza e conservação</option>
              </select>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <span className="text-red-700 whitespace-pre-line">{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-700" />
              <span className="text-green-700 whitespace-pre-line">{success}</span>
            </div>
          )}

          <div className="mb-4 space-y-3">
            <div className="flex gap-3">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Buscar por nome ou código..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <select
                value={filterCategoria}
                onChange={(e) => setFilterCategoria(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md"
              >
                <option value="">Todas as categorias</option>
                {categoriasFiltradas.map((categoria) => (
                  <option key={categoria} value={categoria}>
                    {categoria}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {filteredMaterias.length} {textoItemLista} encontrado(s)
              </span>
              <button
                onClick={toggleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {selectedMaterias.length === filteredMaterias.length &&
                filteredMaterias.length > 0
                  ? 'Desmarcar todas'
                  : 'Selecionar todas'}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Carregando catálogo...</span>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
              {filteredMaterias.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  Nenhum item encontrado com os filtros atuais
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        <input
                          type="checkbox"
                          checked={
                            filteredMaterias.length > 0 &&
                            filteredMaterias.every((materia) =>
                              selectedMaterias.includes(getMateriaIdentifier(materia))
                            )
                          }
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Código
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Nome
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Tipo
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Categoria
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredMaterias.map((materia) => {
                      const materiaId = getMateriaIdentifier(materia);
                      return (
                        <tr key={materia.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedMaterias.includes(materiaId)}
                              onChange={() => toggleMateriaSelection(materia)}
                            />
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {materia.codigo || materia.codigo_produto || materia.codigo_origem || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {materia.nome || materia.nome_produto || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                              {labelTipoBadge(materia)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {materia.categoria || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={importing}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleAssociateMaterias}
              disabled={importing || selectedMaterias.length === 0}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center"
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Associando...
                </>
              ) : (
                `Associar ${selectedMaterias.length > 0 ? `(${selectedMaterias.length})` : ''}`
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssociateProductsModal;

 
